import { DateTime } from "luxon";
import { SummaryPeriod, SummaryPeriodType } from "../types/SleepLogSummary";

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const KEY_REGEX = /^(\d{4}-\d{2}-\d{2})#(week|month)$/;

/**
 * Period helpers for summaries. Mirrors sleepvue-service/src/utils/SummaryPeriodUtils.ts.
 * Diary dates are timezone-less "YYYY-MM-DD" strings, so math is done in UTC. Weeks always start on Monday.
 */
export const SummaryPeriodUtils = {
    isValidDate(date: string): boolean {
        return DATE_REGEX.test(date) && DateTime.fromISO(date, { zone: "utc" }).isValid;
    },

    addDays(date: string, days: number): string {
        return DateTime.fromISO(date, { zone: "utc" }).plus({ days }).toISODate();
    },

    periodFor(type: SummaryPeriodType, date: string): SummaryPeriod {
        const dt = DateTime.fromISO(date, { zone: "utc" });

        if (type === "week") {
            const start = dt.set({ weekday: 1 }).startOf("day");
            return { type, startDate: start.toISODate(), endDate: start.plus({ days: 6 }).toISODate() };
        }

        return { type, startDate: dt.startOf("month").toISODate(), endDate: dt.endOf("month").toISODate() };
    },

    periodFromStart(type: SummaryPeriodType, startDate: string): SummaryPeriod | undefined {
        if (!SummaryPeriodUtils.isValidDate(startDate)) {
            return undefined;
        }

        const period = SummaryPeriodUtils.periodFor(type, startDate);
        return period.startDate === startDate ? period : undefined;
    },

    isCompleted(period: SummaryPeriod, today: string): boolean {
        return period.endDate < today;
    },

    priorPeriod(period: SummaryPeriod): SummaryPeriod {
        return SummaryPeriodUtils.periodFor(period.type, SummaryPeriodUtils.addDays(period.startDate, -1));
    },

    nextPeriod(period: SummaryPeriod): SummaryPeriod {
        return SummaryPeriodUtils.periodFor(period.type, SummaryPeriodUtils.addDays(period.endDate, 1));
    },

    toKey(period: Pick<SummaryPeriod, "type" | "startDate">): string {
        return `${period.startDate}#${period.type}`;
    },

    fromKey(key: string): SummaryPeriod | undefined {
        const match = KEY_REGEX.exec(key);
        return match ? SummaryPeriodUtils.periodFromStart(match[2] as SummaryPeriodType, match[1]) : undefined;
    },

    /** Completed week and month periods that contain at least one of the dates, newest first. */
    completedPeriodsForDates(dates: string[], today: string): SummaryPeriod[] {
        const periods = new Map<string, SummaryPeriod>();

        for (const date of dates) {
            if (!SummaryPeriodUtils.isValidDate(date)) {
                continue;
            }

            for (const type of ["week", "month"] as const) {
                const period = SummaryPeriodUtils.periodFor(type, date);

                if (SummaryPeriodUtils.isCompleted(period, today)) {
                    periods.set(SummaryPeriodUtils.toKey(period), period);
                }
            }
        }

        return [...periods.values()].sort((a, b) => b.endDate.localeCompare(a.endDate) || a.type.localeCompare(b.type));
    },

    /** The most recent completed periods of a type, newest first. */
    recentCompletedPeriods(type: SummaryPeriodType, today: string, count: number): SummaryPeriod[] {
        const periods: SummaryPeriod[] = [];
        let period = SummaryPeriodUtils.priorPeriod(SummaryPeriodUtils.periodFor(type, today));

        while (periods.length < count) {
            periods.push(period);
            period = SummaryPeriodUtils.priorPeriod(period);
        }

        return periods;
    },

    /** Only the latest completed calendar week and month are loaded automatically. */
    latestCompletedPeriods(today: string): SummaryPeriod[] {
        return [
            ...SummaryPeriodUtils.recentCompletedPeriods("week", today, 1),
            ...SummaryPeriodUtils.recentCompletedPeriods("month", today, 1),
        ];
    },

    /** "Week of Oct 6 – 12", "Week of Sep 29 – Oct 5", or "September 2026". */
    formatTitle(period: Pick<SummaryPeriod, "type" | "startDate" | "endDate">): string {
        const start = DateTime.fromISO(period.startDate, { zone: "utc" });
        const end = DateTime.fromISO(period.endDate, { zone: "utc" });

        if (period.type === "month") {
            return start.toFormat("LLLL yyyy");
        }

        const startText = start.toFormat("LLL d");
        const endText = start.month === end.month ? end.toFormat("d") : end.toFormat("LLL d");
        const yearText = start.year !== end.year || start.year !== DateTime.now().year ? `, ${end.year}` : "";

        return `Week of ${startText} – ${endText}${yearText}`;
    },

    routeFor(period: Pick<SummaryPeriod, "type" | "startDate">): string {
        return `/sleep-logs/summaries/${period.type}/${period.startDate}`;
    },
};

export default SummaryPeriodUtils;
